/**
 * EPL Lab Runner - Interface para que estudiantes ejecuten laboratorios
 * 
 * Funcionalidades:
 * - Cargar laboratorio por ID
 * - Mostrar información y metadata del lab
 * - Renderizar formulario dinámico basado en fields
 * - Gestionar sesión activa con auto-save
 * - Validar respuestas en tiempo real
 * - Enviar para evaluación IA
 * - Mostrar resultados y feedback
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import {
  Play, 
  Save, 
  Send, 
  Clock, 
  User, 
  BarChart3, 
  AlertCircle,
  CheckCircle,
  Loader,
  ArrowLeft,
  BookOpen,
  Target,
  Timer,
  Award,
  Download
} from 'lucide-react';

import { useEplLab, useEplSession } from '../hooks/useEpl';
import EplFieldDesigner from '../components/EplFieldDesigner';
import { eplApi } from '../services/epl_api';
import { generateEPLExecutiveReport } from '../utils/pdfGenerator';

const EplLabRunner = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Detectar parámetros de URL para carga directa de resultados
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const stateIdParam = searchParams.get('stateId');

  const [currentStep, setCurrentStep] = useState('loading'); // loading, info, running, results
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Estados para Feedback IA e intentos
  const [aiAttempts, setAiAttempts] = useState(0); // Contador de intentos (máximo 3)
  const [aiFeedback, setAiFeedback] = useState(null); // Feedback de la IA
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  // 🔍 Estado para debug de datos RAW (solo davidprudencio@hotmail.com)
  const [showRawDebug, setShowRawDebug] = useState(false);
  const [rawDebugData, setRawDebugData] = useState(null);
  const [loadingRawDebug, setLoadingRawDebug] = useState(false);

  const { lab, loading: labLoading, error: labError, actions: labActions } = useEplLab(labId);
  const { 
    session, 
    responses, 
    loading: sessionLoading, 
    error: sessionError, 
    actions: sessionActions 
  } = useEplSession();

  // Timer para mostrar tiempo transcurrido
  useEffect(() => {
    let interval;
    if (startTime && currentStep === 'running') {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, currentStep]);

  // Cargar datos iniciales
  useEffect(() => {
    if (lab && !labLoading) {
      console.log('🔍 [DEBUG] Lab cargado en Runner:', lab);
      console.log('🔍 [DEBUG] lab.tags:', lab.tags);
      console.log('🔍 [DEBUG] lab.metadata?.tags:', lab.metadata?.tags);
      console.log('🔍 [DEBUG] URL params - view:', viewParam, 'stateId:', stateIdParam);
      
      // Si viene con view=results y stateId, cargar directamente resultados
      if (viewParam === 'results' && stateIdParam) {
        console.log('✅ Detectado view=results - Cargando sesión completada...');
        loadCompletedSession(stateIdParam);
      } else {
        console.log('🆕 No hay view=results - Mostrando pantalla de info');
        setCurrentStep('info');
      }
    }
  }, [lab, labLoading, viewParam, stateIdParam]);
  
  // Función para cargar sesión completada
  const loadCompletedSession = async (stateId) => {
    try {
      setCurrentStep('loading');
      // Cargar la sesión completada usando el stateId
      const result = await sessionActions.loadSession(stateId);
      
      // Cargar el número de intentos reales que usó el usuario
      if (result?.data?.progress?.aiAttempts) {
        setAiAttempts(result.data.progress.aiAttempts);
        console.log('✅ Intentos cargados desde sesión:', result.data.progress.aiAttempts);
      }
      
      setCurrentStep('results');
    } catch (error) {
      console.error('Error cargando sesión completada:', error);
      setCurrentStep('info'); // Fallback a pantalla de info
    }
  };

  // Sincronizar respuestas de sesión con form data
  useEffect(() => {
    if (responses && Object.keys(responses).length > 0) {
      setFormData(responses);
    }
  }, [responses]);
  
  // Sincronizar intentos y feedback desde la sesión cargada
  useEffect(() => {
    if (currentStep === 'results' && session) {
      console.log('🔍 DEBUG session completo:', { 
        hasProgress: !!session.progress,
        hasAiResults: !!session.aiResults,
        aiAttempts: session.progress?.aiAttempts 
      });
      
      // Cargar intentos desde progress
      if (session.progress?.aiAttempts) {
        setAiAttempts(session.progress.aiAttempts);
        console.log('✅ Intentos sincronizados desde sesión:', session.progress.aiAttempts);
      }
      
      // Cargar feedback de IA desde raíz del documento (✅ UBICACIÓN CORRECTA)
      if (session.aiResults) {
        setAiFeedback(session.aiResults);
        console.log('✅ Feedback IA sincronizado desde session.aiResults:', session.aiResults);
      } else {
        console.log('⚠️ NO hay aiResults en session (raíz)');
      }
    }
  }, [session, currentStep]);

  const handleStartLab = async () => {
    try {
      setCurrentStep('loading');
      await sessionActions.startSession(labId);
      setStartTime(Date.now());
      setCurrentStep('running');
    } catch (error) {
      console.error('Error starting lab:', error);
      alert('Error al iniciar el laboratorio: ' + error.message);
      setCurrentStep('info');
    }
  };

  const handleFieldChange = useCallback((fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Limpiar errores de validación para este campo
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const validateForm = () => {
    const errors = {};

    if (!lab?.fields) return { isValid: true, errors: {} };

    lab.fields.forEach(field => {
      const value = formData[field.id];
      
      // Campo obligatorio
      if (field.required && (!value || value.toString().trim() === '')) {
        errors[field.id] = 'Este campo es obligatorio';
        return;
      }

      // Validaciones específicas por tipo
      if (value && field.validation) {
        const { minLength, maxLength, pattern } = field.validation;
        
        if (minLength && value.toString().length < minLength) {
          errors[field.id] = `Debe tener al menos ${minLength} caracteres`;
        }
        
        if (maxLength && value.toString().length > maxLength) {
          errors[field.id] = `No puede exceder ${maxLength} caracteres`;
        }
        
        if (pattern && !new RegExp(pattern).test(value.toString())) {
          errors[field.id] = field.validation.customMessage || 'Formato inválido';
        }
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const handleAutoSave = useCallback(async () => {
    if (!session || Object.keys(formData).length === 0) return;

    try {
      await sessionActions.updateResponses(formData);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [session, formData, sessionActions]);

  // Auto-save cada 30 segundos
  useEffect(() => {
    const interval = setInterval(handleAutoSave, 30000);
    return () => clearInterval(interval);
  }, [handleAutoSave]);

  const handleSubmit = async () => {
    const validation = validateForm();
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Calcular nuevo número de intentos ANTES de guardar
      const newAttemptCount = aiAttempts + 1;
      
      console.log(`📊 Guardando lab - Intento ${newAttemptCount} de 3`);
      
      // Guardar respuestas finales CON el contador de intentos actualizado
      await sessionActions.updateResponses(formData, { 
        aiAttempts: newAttemptCount 
      });
      
      // Actualizar estado local DESPUÉS de guardar exitosamente
      setAiAttempts(newAttemptCount);
      
      // Cambiar a vista de resultados
      setCurrentStep('results');

      // Solicitar feedback de IA automáticamente si está habilitado
      if (lab.aiConfig?.enableAI) {
        console.log('🤖 Lab tiene IA habilitada, solicitando feedback...');
        await handleRequestFeedback();
      } else {
        console.log('⚠️ Lab NO tiene IA habilitada');
      }
      
    } catch (error) {
      console.error('Error submitting lab:', error);
      alert('Error al enviar el laboratorio: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Solicitar feedback de IA
   */
  const handleRequestFeedback = async () => {
    try {
      setLoadingFeedback(true);
      setAiFeedback(null);

      console.log('🤖 Solicitando feedback de IA...');
      console.log('Lab ID:', labId);
      console.log('Session ID:', session?.id || session?._id);
      console.log('Responses:', formData);

      // Llamar al endpoint de evaluación IA
      const response = await eplApi.evaluateWithAI(
        labId,
        session?.id || session?._id,
        formData
      );

      console.log('✅ Feedback recibido:', response.data);
      setAiFeedback(response.data);

      // 🔄 RECARGAR SESIÓN para obtener datos actualizados de MongoDB
      console.log('🔄 Recargando sesión desde backend...');
      await sessionActions.loadSession(session?.id || session?._id);
      console.log('✅ Sesión recargada con datos actualizados');

    } catch (error) {
      console.error('❌ Error obteniendo feedback de IA:', error);
      setAiFeedback({
        error: true,
        message: error.message || 'Error al generar feedback. Inténtalo nuevamente.'
      });
    } finally {
      setLoadingFeedback(false);
    }
  };

  // Función para generar y descargar reporte PDF ejecutivo usando generador compartido
  const generateExecutiveReport = useCallback(async () => {
    if (!lab || !formData) return;

    try {
      console.log('📄 Generando PDF para:', user?.nombre);
      
      // Preparar respuestas con configuración de campos (para matrices)
      const responses = [];
      
      if (lab.fields) {
        lab.fields.forEach(field => {
          const value = formData[field.id];
          if (value !== undefined) {
            responses.push({
              fieldLabel: field.label || field.placeholder || 'Sin título',
              fieldValue: value,
              fieldConfig: field.premiumConfig ? {
                rowLabels: field.premiumConfig.rowLabels,
                colLabels: field.premiumConfig.columnLabels || field.premiumConfig.colLabels,
                fieldType: field.premiumConfig.fieldType || field.type
              } : null
            });
          }
        });
      }
      
      // Preparar datos para el generador compartido
      const pdfData = {
        labName: lab.name || 'Laboratorio EPL',
        userName: user?.nombre || 'Estudiante',
        userEmail: user?.email || 'N/A',
        systemCard: lab?.systemCard || 'innovafast',
        completedAt: new Date(),
        aiResults: {
          score: aiFeedback?.score,
          feedback: aiFeedback?.feedback || 'Pendiente de evaluación',
          recommendations: aiFeedback?.recommendations || []
        },
        responses,
        attempts: aiAttempts || 1,
        timeSpent: elapsedTime
      };
      
      // Generar PDF usando la función compartida
      await generateEPLExecutiveReport(pdfData);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF: ' + error.message);
    }
  }, [lab, formData, elapsedTime, aiFeedback, aiAttempts, user]);

  // 🔍 Función para obtener datos RAW de evaluación IA (solo davidprudencio@hotmail.com)
  const handleShowRawDebug = async () => {
    // Verificar que el usuario sea davidprudencio@hotmail.com
    if (user?.email !== 'davidprudencio@hotmail.com') {
      alert('Esta funcionalidad es exclusiva para el administrador del sistema.');
      return;
    }

    try {
      setLoadingRawDebug(true);
      setShowRawDebug(true);

      const stateId = session?.id || session?._id;
      console.log('🔍 Obteniendo datos RAW para sesión:', stateId);

      const response = await eplApi.getDebugRawData(stateId);
      
      console.log('✅ Datos RAW recibidos:', response.data);
      setRawDebugData(response.data);

    } catch (error) {
      console.error('❌ Error obteniendo datos RAW:', error);
      alert('Error al obtener datos RAW: ' + error.message);
      setShowRawDebug(false);
    } finally {
      setLoadingRawDebug(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'Principiante';
      case 'intermediate': return 'Intermedio';
      case 'advanced': return 'Avanzado';
      default: return 'No especificado';
    }
  };

  if (labLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Cargando laboratorio...</p>
        </div>
      </div>
    );
  }

  if (labError) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-red-900">Error al cargar laboratorio</h2>
        </div>
        <p className="text-red-700 mb-4">{labError}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Volver al Dashboard Principal
        </button>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-yellow-600" />
          <h2 className="text-xl font-semibold text-yellow-900">Laboratorio no encontrado</h2>
        </div>
        <p className="text-yellow-700 mb-4">
          El laboratorio solicitado no existe o no tienes permisos para accederlo.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
        >
          Volver al Dashboard Principal
        </button>
      </div>
    );
  }

  // Función para obtener el nombre del sistema
  const getSystemName = (systemCard) => {
    const systems = {
      'innovafast': 'InnovaFast',
      'innovatech': 'InnovaTech',
      'transforma360': 'Transforma360'
    };
    return systems[systemCard] || 'Laboratorios';
  };

  // Función para navegar de vuelta a la lista de labs del sistema
  const handleBackToLabs = () => {
    if (lab?.systemCard) {
      navigate(`/epl/labs/${lab.systemCard}`);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 p-4 md:p-6 overflow-y-auto pb-24">
      <div className="max-w-4xl mx-auto">
        
        {/* Header con breadcrumb dinámico */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex flex-col gap-1">
            <button
              onClick={handleBackToLabs}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium hidden sm:inline">Laboratorios {lab?.systemCard && getSystemName(lab.systemCard)}</span>
              <span className="font-medium sm:hidden">Volver</span>
            </button>
            <div className="text-xs text-gray-500 ml-7 hidden md:block">
              Dashboard &gt; {lab?.systemCard && getSystemName(lab.systemCard)} &gt; {lab?.name || 'Laboratorio'}
            </div>
          </div>

          {currentStep === 'running' && (
            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-1 md:gap-2 text-gray-600">
                <Timer className="w-4 h-4" />
                <span className="font-mono text-xs md:text-sm">{formatTime(elapsedTime)}</span>
              </div>
              
              <button
                onClick={handleAutoSave}
                className="hidden sm:flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                <Save className="w-4 h-4" />
                Guardar Progreso
              </button>
            </div>
          )}
        </div>

        {/* Lab Info Step */}
        {currentStep === 'info' && (
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <div className="text-center mb-8">
              <BookOpen className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{lab.name || lab.title || 'Laboratorio EPL'}</h1>
              <p className="text-gray-600 text-lg mb-4">{lab.description || 'Laboratorio interactivo EPL'}</p>
              
              {(() => {
                const tags = lab.metadata?.tags || lab.tags || [];
                return tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
              <div className="text-center p-3 md:p-4 bg-gray-50 rounded-lg">
                <Timer className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-sm text-gray-600">Tiempo Estimado</div>
                <div className="text-lg font-semibold text-gray-900">
                  {lab.metadata?.estimatedTime || lab.estimatedTime || 15} min
                </div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-sm text-gray-600">Dificultad</div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(lab.metadata?.difficulty || lab.difficulty || 'beginner')}`}>
                  {getDifficultyText(lab.metadata?.difficulty || lab.difficulty || 'beginner')}
                </div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <div className="text-sm text-gray-600">Campos</div>
                <div className="text-lg font-semibold text-gray-900">
                  {lab.fields?.length || 0}
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={handleStartLab}
                disabled={sessionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium text-lg flex items-center gap-3 mx-auto transition-colors disabled:opacity-50"
              >
                {sessionLoading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                Comenzar Laboratorio
              </button>
            </div>
          </div>
        )}

        {/* Lab Running Step */}
        {currentStep === 'running' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{lab.title}</h1>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Sesión: {session?.id?.substring(0, 8)}...
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    session?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {session?.status || 'Activa'}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              {lab.fields && lab.fields.length > 0 ? (
                <DynamicLabForm
                  fields={lab.fields}
                  formData={formData}
                  validationErrors={validationErrors}
                  onChange={handleFieldChange}
                />
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Este laboratorio no tiene campos configurados</p>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {Object.keys(validationErrors).length > 0 && (
                    <span className="text-red-600">
                      {Object.keys(validationErrors).length} campo(s) con errores
                    </span>
                  )}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || Object.keys(formData).length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Enviar Laboratorio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Step */}
        {currentStep === 'results' && (
          <div className="bg-white rounded-lg shadow-sm border p-8 relative">
            {/* Botones en esquina superior derecha */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {/* Botón PDF */}
              <button
                onClick={generateExecutiveReport}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 transition-colors shadow-lg"
                title="Descargar reporte ejecutivo en PDF"
              >
                <Download className="w-3 h-3" />
                PDF
              </button>

              {/* 🔍 Botón DEBUG RAW (solo para davidprudencio@hotmail.com) */}
              {user?.email === 'davidprudencio@hotmail.com' && (
                <button
                  onClick={handleShowRawDebug}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 transition-colors shadow-lg"
                  title="Ver prompt y respuesta RAW de ChatGPT"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  RAW
                </button>
              )}
            </div>

            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                ¡Laboratorio Completado!
              </h1>
              <p className="text-gray-600">
                Tu trabajo ha sido enviado exitosamente para evaluación
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Tiempo Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatTime(elapsedTime)}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Campos Completados</div>
                <div className="text-2xl font-bold text-gray-900">
                  {Object.keys(formData).length}
                </div>
              </div>
            </div>

            {/* Feedback IA Section - Compacto en móvil */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-3 md:p-6 rounded-lg mb-4 md:mb-6 shadow-sm">
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 text-sm md:text-base mb-2 md:mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Feedback IA Pedagógica
                </h3>
                  
                  {/* Contenido de Feedback IA - Optimizado para móvil */}
                  <div className="space-y-3 md:space-y-4">
                    {loadingFeedback ? (
                      <div className="text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <div className="animate-pulse flex space-x-1">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                          <span className="ml-2">Generando feedback personalizado...</span>
                        </span>
                      </div>
                    ) : aiFeedback?.error ? (
                      <div className="flex items-start gap-2 text-red-700 p-3 bg-red-50 rounded-lg border border-red-200">
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm"><strong>Error:</strong> {aiFeedback.message}</p>
                          <button
                            onClick={handleRequestFeedback}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                          >
                            Reintentar
                          </button>
                        </div>
                      </div>
                    ) : aiFeedback ? (
                      <div className="space-y-3 md:space-y-4">
                        {/* Score - Compacto en móvil */}
                        {aiFeedback.score !== null && aiFeedback.score !== undefined && (
                          <div className="flex items-center gap-2 md:gap-3 pb-2 md:pb-3 border-b">
                            <div className="text-3xl md:text-4xl font-bold text-blue-600">
                              {aiFeedback.score}
                            </div>
                            <div className="text-xs md:text-sm text-gray-600">
                              <div className="font-medium">Puntuación</div>
                              <div>sobre 100</div>
                            </div>
                          </div>
                        )}

                        {/* Feedback - Sin marco en móvil */}
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm md:text-base mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Análisis
                          </h4>
                          <p className="text-sm md:text-base text-gray-700 whitespace-pre-wrap leading-relaxed">{aiFeedback.feedback}</p>
                        </div>

                        {/* Recomendaciones - Compactas en móvil */}
                        {aiFeedback.recommendations && aiFeedback.recommendations.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 text-sm md:text-base mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4 md:w-5 md:h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              Recomendaciones
                            </h4>
                            <ul className="space-y-1.5 md:space-y-2">
                              {aiFeedback.recommendations.map((rec, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-sm md:text-base text-gray-700">{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Metadata - Oculto en móvil */}
                        {aiFeedback.metadata && (
                          <div className="hidden md:block text-xs text-gray-500 pt-2 border-t">
                            Generado con {aiFeedback.metadata.model} • {aiFeedback.metadata.tokensUsed} tokens
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-700 p-3 md:p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <strong className="text-sm md:text-base text-gray-900">El feedback aparecerá automáticamente</strong>
                        </div>
                        <p className="text-xs md:text-sm text-gray-500">
                          Después de enviar tu laboratorio, la IA generará un análisis detallado con recomendaciones personalizadas.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Contador de intentos - Solo si hay feedback, compacto en móvil */}
                  {aiFeedback && (
                    <div className="mt-2 md:mt-3 flex items-center gap-2 text-xs md:text-sm">
                      <span className="text-gray-600">Intento {aiAttempts} de 3</span>
                      <div className="flex gap-1">
                        {[1, 2, 3].map((attempt) => (
                          <div
                            key={attempt}
                            className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full ${
                              attempt <= aiAttempts ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Botones de acción - Jerarquía visual clara */}
            <div className="flex flex-col items-center gap-2 md:gap-3">
              {/* Botón primario - Ajustar respuestas (si hay intentos) */}
              {aiAttempts < 3 && (
                <button
                  onClick={() => setCurrentStep('running')}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Ajustar Respuestas
                </button>
              )}

              {/* Botones secundarios */}
              <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-3 w-full sm:w-auto">
                <button
                  onClick={handleBackToLabs}
                  className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver a Laboratorios
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors border border-gray-300 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Dashboard Principal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {currentStep === 'loading' && (
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-gray-600">Procesando...</p>
            </div>
          </div>
        )}

      </div>

      {/* 🔍 MODAL DEBUG RAW DATA (solo davidprudencio@hotmail.com) */}
      {showRawDebug && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Debug: Datos RAW de Evaluación IA</h2>
                  <p className="text-sm text-gray-600">Prompt completo y respuesta de ChatGPT</p>
                </div>
              </div>
              <button
                onClick={() => setShowRawDebug(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {loadingRawDebug ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-purple-600" />
                  <span className="ml-3 text-gray-600">Cargando datos RAW...</span>
                </div>
              ) : rawDebugData ? (
                <>
                  {/* Metadata */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Metadata de Sesión
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Session ID:</span>
                        <p className="font-mono text-xs bg-white px-2 py-1 rounded mt-1">{rawDebugData.sessionId}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Lab ID:</span>
                        <p className="font-mono text-xs bg-white px-2 py-1 rounded mt-1">{rawDebugData.labId}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Score:</span>
                        <p className="font-bold text-lg text-blue-600 mt-1">{rawDebugData.score || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Intentos:</span>
                        <p className="font-bold text-lg text-purple-600 mt-1">{rawDebugData.aiAttempts || 0}/3</p>
                      </div>
                    </div>
                  </div>

                  {/* System Prompt */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      System Prompt (Instrucciones del Sistema)
                    </h3>
                    <pre className="bg-white p-4 rounded text-xs font-mono overflow-x-auto border border-blue-200 whitespace-pre-wrap">
                      {rawDebugData.aiDebugRaw?.systemPrompt || 'No disponible'}
                    </pre>
                  </div>

                  {/* User Prompt */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      User Prompt (Respuestas del Estudiante)
                    </h3>
                    <pre className="bg-white p-4 rounded text-xs font-mono overflow-x-auto border border-green-200 whitespace-pre-wrap">
                      {rawDebugData.aiDebugRaw?.userPrompt || 'No disponible'}
                    </pre>
                  </div>

                  {/* AI Response */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      Respuesta Completa de ChatGPT
                    </h3>
                    <pre className="bg-white p-4 rounded text-xs font-mono overflow-x-auto border border-purple-200 whitespace-pre-wrap">
                      {JSON.stringify(rawDebugData.aiDebugRaw?.fullAiResponse || {}, null, 2)}
                    </pre>
                  </div>

                  {/* Raw API Response */}
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Raw API Response (OpenAI)
                    </h3>
                    <pre className="bg-white p-4 rounded text-xs font-mono overflow-x-auto border border-amber-200 whitespace-pre-wrap">
                      {JSON.stringify(rawDebugData.aiDebugRaw?.rawApiResponse || {}, null, 2)}
                    </pre>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No hay datos RAW disponibles
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(rawDebugData, null, 2));
                  alert('Datos copiados al portapapeles');
                }}
                className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
              >
                Copiar JSON
              </button>
              <button
                onClick={() => setShowRawDebug(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para renderizar el formulario dinámico
const DynamicLabForm = ({ fields, formData, validationErrors, onChange }) => {
  const renderField = (field) => {
    const value = formData[field.id] || '';
    const hasError = validationErrors[field.id];

    const baseClasses = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'
    }`;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={baseClasses}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            className={baseClasses}
          >
            <option value="">Selecciona una opción...</option>
            {field.options?.map((option, index) => {
              // Manejar opciones como objeto {label, value} o string
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              return (
                <option key={index} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-3">
            {field.options?.map((option, index) => {
              // Manejar opciones como objeto {label, value} o string
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              return (
                <label key={index} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name={field.id}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => onChange(field.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-900">{optionLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-3">
            {field.options?.map((option, index) => {
              // Manejar opciones como objeto {label, value} o string
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              const currentArray = Array.isArray(value) ? value : [];
              return (
                <label key={index} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={currentArray.includes(optionValue)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange(field.id, [...currentArray, optionValue]);
                      } else {
                        onChange(field.id, currentArray.filter(v => v !== optionValue));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <span className="text-gray-900">{optionLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case 'matrix':
        const rows = field.premiumConfig?.rows || 3;
        const cols = field.premiumConfig?.columns || 3;
        const rowLabels = field.premiumConfig?.rowLabels || [];
        const colLabels = field.premiumConfig?.columnLabels || [];
        const cellType = field.premiumConfig?.cellType || 'text';
        const matrixValue = value || {};

        return (
          <div className="overflow-x-auto border border-yellow-300 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 p-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded">⭐ PREMIUM</span>
            </div>
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="border border-gray-300 bg-gray-200 p-2"></th>
                  {Array.from({ length: cols }).map((_, colIdx) => (
                    <th key={colIdx} className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium whitespace-nowrap">
                      {colLabels[colIdx] || `Col ${colIdx + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rows }).map((_, rowIdx) => (
                  <tr key={rowIdx}>
                    <td className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium whitespace-nowrap">
                      {rowLabels[rowIdx] || `Fila ${rowIdx + 1}`}
                    </td>
                    {Array.from({ length: cols }).map((_, colIdx) => {
                      const cellKey = `${rowIdx}-${colIdx}`;
                      return (
                        <td key={colIdx} className="border border-gray-300 p-1 bg-white">
                          {cellType === 'checkbox' ? (
                            <input
                              type="checkbox"
                              checked={matrixValue[cellKey] || false}
                              onChange={(e) => onChange(field.id, { ...matrixValue, [cellKey]: e.target.checked })}
                              className="w-4 h-4 mx-auto block"
                            />
                          ) : (
                            <input
                              type={cellType === 'number' ? 'number' : 'text'}
                              value={matrixValue[cellKey] || ''}
                              onChange={(e) => onChange(field.id, { ...matrixValue, [cellKey]: e.target.value })}
                              placeholder={field.placeholder || '...'}
                              className="w-full px-2 py-1 text-sm border-0 focus:ring-1 focus:ring-yellow-400"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'table':
        const tableColumns = field.premiumConfig?.tableColumns || [];
        const maxRows = field.premiumConfig?.maxRows || 10;
        const minRows = field.premiumConfig?.minRows || 1;
        const rowAddable = field.premiumConfig?.rowAddable !== false;
        const tableValue = Array.isArray(value) ? value : [];

        const addRow = () => {
          if (tableValue.length < maxRows) {
            const newRow = {};
            tableColumns.forEach(col => { newRow[col] = ''; });
            onChange(field.id, [...tableValue, newRow]);
          }
        };

        const removeRow = (index) => {
          if (tableValue.length > minRows) {
            onChange(field.id, tableValue.filter((_, i) => i !== index));
          }
        };

        const updateCell = (rowIndex, column, cellValue) => {
          const newTable = [...tableValue];
          newTable[rowIndex] = { ...newTable[rowIndex], [column]: cellValue };
          onChange(field.id, newTable);
        };

        return (
          <div className="border border-yellow-300 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded">⭐ PREMIUM</span>
              {rowAddable && tableValue.length < maxRows && (
                <button
                  type="button"
                  onClick={addRow}
                  className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded"
                >
                  + Agregar Fila
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    {tableColumns.map((col, idx) => (
                      <th key={idx} className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium">
                        {col}
                      </th>
                    ))}
                    {rowAddable && <th className="border border-gray-300 bg-gray-100 p-2 w-20"></th>}
                  </tr>
                </thead>
                <tbody>
                  {tableValue.length === 0 && (
                    <tr>
                      <td colSpan={tableColumns.length + (rowAddable ? 1 : 0)} className="text-center p-4 text-gray-500 italic">
                        No hay filas. Haz clic en "Agregar Fila" para comenzar.
                      </td>
                    </tr>
                  )}
                  {tableValue.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {tableColumns.map((col, colIdx) => (
                        <td key={colIdx} className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={row[col] || ''}
                            onChange={(e) => updateCell(rowIdx, col, e.target.value)}
                            className="w-full px-2 py-1 text-sm border-0 focus:ring-1 focus:ring-yellow-400"
                          />
                        </td>
                      ))}
                      {rowAddable && (
                        <td className="border border-gray-300 p-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeRow(rowIdx)}
                            disabled={tableValue.length <= minRows}
                            className="text-red-600 hover:text-red-800 disabled:text-gray-400 text-xs"
                          >
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'ranking':
        const rankItems = field.premiumConfig?.items || [];
        const rankValue = Array.isArray(value) ? value : [...rankItems];

        const moveItem = (fromIndex, toIndex) => {
          const newRank = [...rankValue];
          const [movedItem] = newRank.splice(fromIndex, 1);
          newRank.splice(toIndex, 0, movedItem);
          onChange(field.id, newRank);
        };

        return (
          <div className="border border-yellow-300 rounded-lg bg-gradient-to-br from-yellow-50 to-amber-50 p-4">
            <div className="mb-3">
              <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded">⭐ PREMIUM</span>
              <p className="text-xs text-gray-600 mt-2">Arrastra para reordenar o usa los botones ↑ ↓</p>
            </div>
            <div className="space-y-2">
              {rankValue.map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-white p-3 rounded border border-gray-200">
                  <span className="font-bold text-yellow-600 text-lg w-8">{index + 1}.</span>
                  <span className="flex-1 text-gray-900">{item}</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveItem(index, index - 1)}
                      disabled={index === 0}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(index, index + 1)}
                      disabled={index === rankValue.length - 1}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section_step':
        const sectionTitle = field.premiumConfig?.sectionTitle || 'Sección';
        const sectionDesc = field.premiumConfig?.sectionDescription || '';
        
        return (
          <div className="border-l-4 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-r-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded">⭐ PREMIUM</span>
              <h3 className="font-bold text-gray-900">{sectionTitle}</h3>
            </div>
            {sectionDesc && (
              <p className="text-sm text-gray-700 mt-2">{sectionDesc}</p>
            )}
          </div>
        );

      default:
        return (
          <div className="text-gray-500 italic">
            Tipo de campo no soportado: {field.type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {fields.map((field) => (
        <div key={field.id}>
          <label className="block text-sm font-medium text-gray-700 mb-2 break-words">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {field.description && (
            <div className="text-sm text-gray-600 mb-3 prose prose-sm max-w-full overflow-x-hidden break-words">
              <ReactMarkdown>{field.description}</ReactMarkdown>
            </div>
          )}
          
          {renderField(field)}
          
          {validationErrors[field.id] && (
            <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {validationErrors[field.id]}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default EplLabRunner;